/**
 * Camera and controls configuration
 */
export interface CameraViewOffsetConfig {
  /** Whether the principal point shift is applied */
  enabled: boolean;

  /** Whether the principal point shift should animate */
  animate?: boolean;

  /** Duration of the principal point animation in milliseconds */
  animationDurationMs?: number;

  /** Pixels trimmed from the top edge */
  top: number;

  /** Pixels trimmed from the right edge */
  right: number;

  /** Pixels trimmed from the bottom edge */
  bottom: number;

  /** Pixels trimmed from the left edge */
  left: number;
}

export interface CameraConfig {
  /** Field of view in degrees */
  fov: number;

  /** Degrees rotated when dragging across the entire viewport width */
  lookSpeedHorizontal: number;
  
  /** Degrees rotated when dragging across the entire viewport height */
  lookSpeedVertical: number;

  /** Drag inertia factor (0 disables, values near 1 keep momentum longer) */
  dragInertia: number;
  
  /** The movement speed */
  movementSpeed: number;

  /** Whether the camera is automatically moved forward or not */
  autoForward: boolean;

  /** Whether orbit view should continuously rotate around the target */
  orbitAutoMove: boolean;

  /** Direction for automatic orbit movement */
  orbitAutoMoveDirection: "left" | "right";

  /** Angular speed in radians per second for automatic orbit movement */
  orbitAutoMoveSpeed: number;

  /** Minimum distance allowed for orbit controls */
  orbitMinDistance: number;

  /** Maximum distance allowed for orbit controls */
  orbitMaxDistance: number;

  /** Minimum vertical angle for orbit controls (radians) */
  orbitMinPolarAngle: number;

  /** Maximum vertical angle for orbit controls (radians) */
  orbitMaxPolarAngle: number;

  /** Whether it's possible to look around or not */
  activeLook: boolean;

  /** Whether or not the camera's height influences the forward movement speed */
  heightSpeed: boolean;

  /** Determines how much faster the camera moves when its height changes */
  heightCoef: number;

  /** Lower height limit used for movement speed adjustment */
  speedHeightMin: number;

  /** Upper height limit used for movement speed adjustment */
  speedHeightMax: number;
  
  /** Scale factor that converts pinch distance delta (px) into movement */
  pinchMoveSpeed: number;
  
  /** Whether it's possible to vertically look around or not */
  lookVertical: boolean;
  
  /** Whether the camera's vertical look is constrained */
  constrainVertical: boolean;
  
  /** Minimum vertical angle in radians (0 to Math.PI) */
  verticalMin: number;
  
  /** Maximum vertical angle in radians (0 to Math.PI) */
  verticalMax: number;
  
  /** Minimum camera Y position in world space. Camera cannot move below this height */
  clampMinHeight: number;
  
  /** Maximum camera Y position in world space. Camera cannot move above this height */
  clampMaxHeight: number;

  /** Maximum camera Y at the farthest orbit zoom. Interpolated with clampMaxHeight based on distance */
  clampMaxHeightFar: number;

  /** Default center point used when switching to orbit view */
  orbitCenter: {
    x: number;
    y: number;
    z: number;
  };

  /** Virtual viewport definition used to offset the projection center */
  viewOffset: CameraViewOffsetConfig;
}

/**
 * Default camera configuration
 */
export const defaultCameraConfig: CameraConfig = {
  fov: 65,
  lookSpeedHorizontal: 50,
  lookSpeedVertical: 15,
  dragInertia: 0.92,
  movementSpeed: 0.15,
  autoForward: false,
  orbitAutoMove: false,
  orbitAutoMoveDirection: "left",
  orbitAutoMoveSpeed: 5,
  orbitMinDistance: 0,
  orbitMaxDistance: 10,
  orbitMinPolarAngle: 0,
  orbitMaxPolarAngle: Math.PI,
  activeLook: true,
  heightSpeed: false,
  heightCoef: 1,
  speedHeightMin: 0,
  speedHeightMax: 1,
  pinchMoveSpeed: 0.002,
  lookVertical: true,
  constrainVertical: true,
  verticalMin: Math.PI * 1 / 3,
  verticalMax: Math.PI * 2 / 3,
  clampMinHeight: 0.08,
  clampMaxHeight: 0.43,
  clampMaxHeightFar: 0.20,
  orbitCenter: { x: -0.32, y: 0.11, z: 0.48 },
  viewOffset: {
    enabled: false,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
};
